import React, { useState, useEffect } from 'react'
import './OfficeInfo.css';
import 'react-icons/ti';

const Accordion = ({ items = [] }) => {
  const [activeIndex, setActiveIndex] = React.useState(null);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="faq-list">
      {items.map((item, index) => (
        <div
          key={index}
          className={`faq-item ${activeIndex === index ? 'open' : ''}`}
        >
          <div className="faq-question" onClick={() => toggleAccordion(index)}>
            <h4>{item.question}</h4>
            <span className="faq-icon"></span>
          </div>
          <div
            className="faq-answer"
            style={{
              maxHeight: activeIndex === index ? '500px' : '0',
              opacity: activeIndex === index ? '1' : '0',
              paddingTop: '1rem'
            }}
          >
            <p>{item.answer}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const SetupStep = ({ step, index, isChecked, onToggle }) => {
  const handleStepClick = (e) => {
    // Don't toggle if clicking on a link
    if (e.target.tagName === 'A') {
      return;
    }
    onToggle(index);
  };

  return (
    <div className={`setup-step ${isChecked ? 'checked' : ''}`} onClick={handleStepClick}>
      <div className={`step-checkbox ${isChecked ? 'checked' : ''}`}>
        {isChecked && <span className="checkmark">✓</span>}
      </div>
      <div className="step-content">
        <h4>{step.title}</h4>
        <p dangerouslySetInnerHTML={{ __html: step.description }}></p>
      </div>
    </div>
  );
};

const SuccessPopup = ({ isVisible, onClose }) => {
  if (!isVisible) return null;

  return (
    <div className="success-popup-overlay" onClick={onClose}>
      <div className="success-popup" onClick={(e) => e.stopPropagation()}>
        <div className="success-icon">🎉</div>
        <h3>You're Ready!</h3>
        <p>All steps completed. You're now ready for your telemedicine appointment!</p>
        <div className="success-button" onClick={onClose} role="button" tabIndex={0}>
          Got it!
        </div>
      </div>
    </div>
  );
};

export const Telemedicine = () => {
    const [checkedSteps, setCheckedSteps] = useState([]);
    const [showSuccess, setShowSuccess] = useState(false);

    const setupSteps = [
        {
            title: "Schedule Your Appointment",
            description: "Call our office at <a href='tel:7818715030'>(781) 871-5030</a> to schedule your telehealth visit"
        },
        {
            title: "Prepare Your Device",
            description: "Ensure you have a device with camera and microphone (smartphone, tablet, or computer)"
        },
        {
            title: "Find a Quiet Space",
            description: "Choose a quiet, private space with good internet connection for your appointment"
        },
        {
            title: "Test Your Equipment",
            description: "Test your device's camera and microphone before the appointment to ensure everything works"
        },
        {
            title: "Join Your Appointment",
            description: "Click the provided link at your scheduled appointment time to enter the virtual waiting room"
        }
    ];

    const toggleStep = (index) => {
        setCheckedSteps(prev => {
            const newChecked = prev.includes(index) 
                ? prev.filter(i => i !== index)
                : [...prev, index];
            return newChecked;
        });
    };

    useEffect(() => {
        if (checkedSteps.length === setupSteps.length) {
            setShowSuccess(true);
        }
    }, [checkedSteps, setupSteps.length]);

    const telemedicineFaqs = [
        {
            question: "How do I setup a telehealth appointment?",
            answer: "Contact our office at (781) 871-5030 to schedule a telehealth visit. Our staff will help determine if your medical concern is appropriate for a virtual visit and guide you through the setup process."
        },
        {
            question: "What technology do I need for a telehealth visit?",
            answer: "You'll need a device with a camera and microphone (smartphone, tablet, or computer), a stable internet connection, and a quiet, private space for your appointment."
        },
        {
            question: "How do I join my telehealth appointment?",
            answer: "You'll receive an email or text with a secure link before your appointment. Click the link at your scheduled time to enter the virtual waiting room. Make sure to test your device's camera and microphone beforehand."
        },
        {
            question: "Are telehealth visits covered by insurance?",
            answer: "Most insurance plans cover telehealth visits. We recommend checking with your insurance provider about specific coverage details and any associated copays."
        },
        {
            question: "What types of visits can be done through telehealth?",
            answer: "Telehealth is suitable for many types of visits including certain follow-ups, medication reviews, and some psych visits. However, some conditions may require in-person evaluation. Please call our office to confirm if this option is available."
        }
    ];

    return (
        <div className="tele-section">
            <div className="tele-container">
                <div className="section-header">
                    <span>Virtual Care</span>
                    <h2>Telemedicine Services</h2>
                </div>

                <div className="tele-details">
                    <h3>Setting Up Your Telehealth Visit</h3>
                    <p className="setup-instructions">Click each step to mark it as complete:</p>
                    <div className="setup-steps">
                        {setupSteps.map((step, index) => (
                            <SetupStep
                                key={index}
                                step={step}
                                index={index}
                                isChecked={checkedSteps.includes(index)}
                                onToggle={toggleStep}
                            />
                        ))}
                    </div>
                    <div className={`progress-indicator ${checkedSteps.length === setupSteps.length ? 'completed' : ''}`}>
                        <span>{checkedSteps.length} of {setupSteps.length} steps completed</span>
                    </div>
                </div>
                
                <div className="faq-section">
                    <div className="faq-container">
                        <h3 className="faq-title">Frequently Asked Questions</h3>
                        <p className="faq-description">Everything you need to know about our telemedicine services. Find answers to common questions and learn how virtual care works at Atlantic Primary Care.</p>
                        <div className="faq-list">
                            <Accordion items={telemedicineFaqs} />
                        </div>
                    </div>
                </div>
            </div>

            <SuccessPopup isVisible={showSuccess} onClose={() => setShowSuccess(false)} />
        </div>
    );
};

export default Telemedicine;
